import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  // Handle scroll for navbar
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setIsScrolled(window.scrollY > 50);
    });
  }

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate textarea length
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      alert('Message must be 500 characters or less');
      setFormStatus('idle');
      return;
    }

    // Get selected values from dropdowns
    const companyTypeSelect = form.querySelector('#company-type') as HTMLSelectElement;
    const revenueSelect = form.querySelector('#revenue') as HTMLSelectElement;
    
    const companyTypeText = companyTypeSelect.options[companyTypeSelect.selectedIndex]?.text || '';
    const revenueText = revenueSelect.options[revenueSelect.selectedIndex]?.text || '';

    // Create URL-encoded data
    const urlEncodedData = new URLSearchParams();
    urlEncodedData.append('name', formData.get('name') as string);
    urlEncodedData.append('email', formData.get('email') as string);
    urlEncodedData.append('phone', formData.get('phone') as string);
    urlEncodedData.append('company', formData.get('company') as string);
    urlEncodedData.append('company-type', companyTypeText);
    urlEncodedData.append('revenue', revenueText);
    urlEncodedData.append('message', message);

    try {
      const response = await fetch('https://readdy.ai/api/form/d64vnm45l058qvghfq30', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://static.readdy.ai/image/bb1f0654749d0fdec59aaa45a4e942a0/d42ca26823536c951591e9fdc3557f83.png" alt="StructureFirst Consulting" className="h-12 w-auto" />
              <div className="flex flex-col">
                <span className={`text-xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>StructureFirst</span>
                <span className={`text-xs ${isScrolled ? 'text-gray-600' : 'text-gray-200'}`}>Consulting</span>
              </div>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-orange-600' : 'text-white hover:text-orange-400'}`}>Home</button>
              <button onClick={() => scrollToSection('services')} className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-orange-600' : 'text-white hover:text-orange-400'}`}>Services</button>
              <button onClick={() => scrollToSection('methodology')} className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-orange-600' : 'text-white hover:text-orange-400'}`}>Methodology</button>
              <button onClick={() => scrollToSection('clients')} className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-orange-600' : 'text-white hover:text-orange-400'}`}>Clients</button>
              <button onClick={() => scrollToSection('why-us')} className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-orange-600' : 'text-white hover:text-orange-400'}`}>Why Choose Us</button>
              <button onClick={() => scrollToSection('contact')} className="px-6 py-2.5 bg-orange-600 text-white text-sm font-semibold rounded-full hover:bg-orange-700 transition-colors whitespace-nowrap">Contact Us</button>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden">
              <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden mt-4 pb-4 bg-white rounded-lg shadow-lg">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Home</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Services</button>
              <button onClick={() => scrollToSection('methodology')} className="block w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Methodology</button>
              <button onClick={() => scrollToSection('clients')} className="block w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Clients</button>
              <button onClick={() => scrollToSection('why-us')} className="block w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Why Choose Us</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left px-4 py-2 text-sm font-medium text-orange-600 hover:bg-gray-50">Contact Us</button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img src="https://public.readdy.ai/ai/img_res/cf81133f0e40d9aeb7a2ebb13790a698.jpg" alt="Organized Construction Site" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <div className="mb-6">
            <span className="text-orange-500 text-sm font-semibold tracking-widest uppercase">Ottawa–Gatineau</span>
          </div>
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-extrabold text-white mb-6 leading-tight">
            Structure Before Scale.<br />
            Clarity Before Growth.<br />
            Systems That Work.
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-10 max-w-3xl mx-auto leading-relaxed">
            Operational structure and financial control for construction companies ready to scale calmly and profitably.
          </p>
          <button onClick={() => scrollToSection('contact')} className="px-10 py-4 bg-orange-600 text-white text-lg font-bold rounded-full hover:bg-orange-700 transition-all hover:shadow-xl hover:scale-105 whitespace-nowrap">
            Book a Diagnostic Call →
          </button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 text-center pb-8">
          <button onClick={() => scrollToSection('services')} className="text-white/80 hover:text-white transition-colors">
            <i className="ri-arrow-down-line text-3xl animate-bounce"></i>
          </button>
        </div>
      </section>

      {/* Problem Statement Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-12 items-start">
            <div className="md:col-span-2">
              <span className="text-orange-600 text-xs font-bold tracking-widest uppercase mb-4 block">Where We Step In</span>
              <h2 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">Running, but not organized.</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                You're generating revenue. But systems are reactive, cash flow is unclear, and you're the bottleneck.
              </p>
            </div>

            <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="w-14 h-14 flex items-center justify-center mb-4">
                  <i className="ri-clipboard-line text-4xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Projects Running</h3>
                <p className="text-gray-600 text-sm">But workflows are inconsistent</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1 sm:mt-10">
                <div className="w-14 h-14 flex items-center justify-center mb-4">
                  <i className="ri-money-dollar-circle-line text-4xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Revenue Coming In</h3>
                <p className="text-gray-600 text-sm">But cash flow feels tight</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1 sm:mt-10">
                <div className="w-14 h-14 flex items-center justify-center mb-4">
                  <i className="ri-calendar-event-line text-4xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Scheduling Exists</h3>
                <p className="text-gray-600 text-sm">But it's reactive, not planned</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="w-14 h-14 flex items-center justify-center mb-4">
                  <i className="ri-user-settings-line text-4xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Owner Involved</h3>
                <p className="text-gray-600 text-sm">But overwhelmed and stuck</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services Section */}
      <section id="services" className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 border border-white/30 text-white text-xs font-semibold tracking-widest uppercase rounded-full mb-6">What We Build</span>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">Systems That Scale</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Service 1 */}
            <div className="bg-white rounded-2xl overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-2">
              <div className="bg-orange-50 p-12 flex items-center justify-center">
                <div className="w-20 h-20 flex items-center justify-center">
                  <i className="ri-flow-chart text-6xl text-orange-600"></i>
                </div>
              </div>
              <div className="bg-gray-900 p-8">
                <h3 className="text-2xl font-bold text-white mb-4">Operational Structure</h3>
                <ul className="space-y-2 text-sm text-gray-400 mb-6">
                  <li>• Workflow mapping</li>
                  <li>• Standard Operating Procedures</li>
                  <li>• Delegation frameworks</li>
                </ul>
                <button className="text-orange-500 text-sm font-semibold hover:text-orange-400 transition-colors whitespace-nowrap">Learn More →</button>
              </div>
            </div>

            {/* Service 2 */}
            <div className="bg-white rounded-2xl overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-2">
              <div className="bg-orange-50 p-12 flex items-center justify-center">
                <div className="w-20 h-20 flex items-center justify-center">
                  <i className="ri-line-chart-line text-6xl text-orange-600"></i>
                </div>
              </div>
              <div className="bg-gray-900 p-8">
                <h3 className="text-2xl font-bold text-white mb-4">Financial Control</h3>
                <ul className="space-y-2 text-sm text-gray-400 mb-6">
                  <li>• Job costing templates</li>
                  <li>• Budget forecasting</li>
                  <li>• AR/AP oversight process</li>
                </ul>
                <button className="text-orange-500 text-sm font-semibold hover:text-orange-400 transition-colors whitespace-nowrap">Learn More →</button>
              </div>
            </div>

            {/* Service 3 */}
            <div className="bg-white rounded-2xl overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-2">
              <div className="bg-orange-50 p-12 flex items-center justify-center">
                <div className="w-20 h-20 flex items-center justify-center">
                  <i className="ri-task-line text-6xl text-orange-600"></i>
                </div>
              </div>
              <div className="bg-gray-900 p-8">
                <h3 className="text-2xl font-bold text-white mb-4">Project Execution</h3>
                <ul className="space-y-2 text-sm text-gray-400 mb-6">
                  <li>• Scheduling systems</li>
                  <li>• Subcontractor coordination</li>
                  <li>• Material planning systems</li>
                </ul>
                <button className="text-orange-500 text-sm font-semibold hover:text-orange-400 transition-colors whitespace-nowrap">Learn More →</button>
              </div>
            </div>

            {/* Service 4 */}
            <div className="bg-white rounded-2xl overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-2">
              <div className="bg-orange-50 p-12 flex items-center justify-center">
                <div className="w-20 h-20 flex items-center justify-center">
                  <i className="ri-rocket-line text-6xl text-orange-600"></i>
                </div>
              </div>
              <div className="bg-gray-900 p-8">
                <h3 className="text-2xl font-bold text-white mb-4">Growth Strategy</h3>
                <ul className="space-y-2 text-sm text-gray-400 mb-6">
                  <li>• Profit margin control</li>
                  <li>• Capacity planning</li>
                  <li>• Expansion readiness systems</li>
                </ul>
                <button className="text-orange-500 text-sm font-semibold hover:text-orange-400 transition-colors whitespace-nowrap">Learn More →</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section id="methodology" className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="text-orange-600 text-xs font-bold tracking-widest uppercase mb-4 block">Our Approach</span>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">The StructureFirst Method™</h2>
            <p className="text-xl text-gray-600">Four phases. Field-tested. No theory.</p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 bottom-0 w-1 bg-orange-600 hidden md:block"></div>

            {/* Phase 1 */}
            <div className="relative mb-16 md:pl-24">
              <div className="absolute left-0 top-0 w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center text-white text-2xl font-bold hidden md:flex">1</div>
              <div className="bg-white border-l-4 border-orange-500 rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                <h3 className="text-3xl font-bold text-gray-900 mb-3">Diagnostic</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">We assess workflow, financial visibility, scheduling, and operational bottlenecks.</p>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li>• Current state analysis</li>
                  <li>• Pain point identification</li>
                  <li>• Opportunity mapping</li>
                </ul>
              </div>
            </div>

            {/* Phase 2 */}
            <div className="relative mb-16 md:pl-24">
              <div className="absolute left-0 top-0 w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center text-white text-2xl font-bold hidden md:flex">2</div>
              <div className="bg-white border-l-4 border-orange-500 rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                <h3 className="text-3xl font-bold text-gray-900 mb-3">Framework Build</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">We design structured workflows, budgeting systems, delegation processes, and reporting structure.</p>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li>• Custom system design</li>
                  <li>• Process documentation</li>
                  <li>• Tool selection & setup</li>
                </ul>
              </div>
            </div>

            {/* Phase 3 */}
            <div className="relative mb-16 md:pl-24">
              <div className="absolute left-0 top-0 w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center text-white text-2xl font-bold hidden md:flex">3</div>
              <div className="bg-white border-l-4 border-orange-500 rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                <h3 className="text-3xl font-bold text-gray-900 mb-3">Implementation</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">We integrate systems into daily operations and train the team.</p>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li>• Team training & onboarding</li>
                  <li>• System integration</li>
                  <li>• Real-time support</li>
                </ul>
              </div>
            </div>

            {/* Phase 4 */}
            <div className="relative md:pl-24">
              <div className="absolute left-0 top-0 w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center text-white text-2xl font-bold hidden md:flex">4</div>
              <div className="bg-white border-l-4 border-orange-500 rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                <h3 className="text-3xl font-bold text-gray-900 mb-3">Stability & Growth</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">We refine, adjust, and prepare the company for controlled expansion.</p>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li>• Performance monitoring</li>
                  <li>• Continuous optimization</li>
                  <li>• Scale preparation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Stat 1 */}
            <div className="bg-orange-600 rounded-3xl p-12 relative overflow-hidden">
              <div className="relative z-10">
                <div className="text-8xl font-bold text-white mb-4">30+</div>
                <div className="text-2xl font-semibold text-white mb-3">Years in Construction</div>
                <p className="text-lg text-white/90 italic">Field-tested systems, not theory</p>
              </div>
              <div className="absolute top-8 right-8 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-2xl text-white"></i>
              </div>
            </div>

            {/* Stat 2 */}
            <div className="bg-emerald-700 rounded-3xl p-12 relative overflow-hidden">
              <div className="relative z-10">
                <div className="text-7xl font-bold text-white mb-4">$1M–$10M</div>
                <div className="text-2xl font-semibold text-white mb-3">Annual Revenue Sweet Spot</div>
                <p className="text-lg text-white/90">Companies generating enough to need structure, small enough to implement it quickly.</p>
              </div>
              <div className="absolute top-8 right-8 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-2xl text-white"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section id="clients" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Who We Work With</h2>
            <p className="text-xl text-gray-600">Construction companies ready for structure</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {/* Client Type 1 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all hover:scale-105">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-hard-hat-line text-5xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">General Contractors</h3>
              <p className="text-sm text-gray-600">Multi-project coordination</p>
            </div>

            {/* Client Type 2 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all hover:scale-105">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-tools-line text-5xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Renovation Companies</h3>
              <p className="text-sm text-gray-600">Residential & commercial</p>
            </div>

            {/* Client Type 3 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all hover:scale-105">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-5xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Restoration Companies</h3>
              <p className="text-sm text-gray-600">Water, fire, mold remediation</p>
            </div>

            {/* Client Type 4 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all hover:scale-105">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-building-line text-5xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Multi-Unit Residential</h3>
              <p className="text-sm text-gray-600">Apartment & condo operators</p>
            </div>

            {/* Client Type 5 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all hover:scale-105">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-key-line text-5xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Property Management</h3>
              <p className="text-sm text-gray-600">Portfolio oversight</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Why StructureFirst</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-full h-96 rounded-2xl overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=experienced%20construction%20professional%20reviewing%20blueprints%20and%20financial%20documents%20in%20modern%20office%20setting%20professional%20consulting%20expertise%20construction%20management%20planning%20organized%20workspace%20natural%20lighting&width=800&height=600&seq=why-choose-expertise&orientation=landscape" alt="Construction Expertise" className="w-full h-full object-cover object-top" />
              </div>
            </div>

            <div>
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">30+ Years Experience</h3>
                    <p className="text-gray-600">Deep expertise in construction, restoration, multi-unit renovation, and building companies from the ground up.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Field-Tested Systems</h3>
                    <p className="text-gray-600">You don't get theory. You get proven systems that work in real construction environments.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Budget Control Expertise</h3>
                    <p className="text-gray-600">Specialized in job costing, forecasting, and cash flow management for construction businesses.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Local to Ottawa–Gatineau</h3>
                    <p className="text-gray-600">We understand the local market, regulations, and business environment.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gray-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-6xl font-bold text-white mb-6">Ready to Build Structure?</h2>
          <p className="text-2xl text-gray-300 mb-10">Book a 45-minute diagnostic call. No pitch. Just clarity.</p>
          <button onClick={() => scrollToSection('contact')} className="px-12 py-5 bg-orange-600 text-white text-xl font-bold rounded-full hover:bg-orange-700 transition-all hover:shadow-2xl hover:shadow-orange-600/50 whitespace-nowrap">
            Book Your Diagnostic Call →
          </button>
          <div className="mt-8 flex items-center justify-center gap-8 text-sm text-gray-400">
            <span className="flex items-center gap-2">
              <i className="ri-check-line text-orange-500"></i>
              30-day implementation starts
            </span>
            <span className="flex items-center gap-2">
              <i className="ri-check-line text-orange-500"></i>
              Field-tested systems
            </span>
            <span className="flex items-center gap-2">
              <i className="ri-check-line text-orange-500"></i>
              Ottawa-Gatineau based
            </span>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Get In Touch</h2>
            <p className="text-xl text-gray-600">Let's discuss how we can bring structure to your construction business.</p>
          </div>

          <form id="contact-form" onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-10 shadow-sm" data-readdy-form>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                <input type="text" id="name" name="name" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm" />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                <input type="email" id="email" name="email" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                <input type="tel" id="phone" name="phone" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm" />
              </div>
              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">Company Name *</label>
                <input type="text" id="company" name="company" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm" />
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="company-type" className="block text-sm font-semibold text-gray-700 mb-2">Company Type *</label>
              <select id="company-type" name="company-type" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm">
                <option value="">Select your company type</option>
                <option value="general-contractor">General Contractor</option>
                <option value="renovation">Renovation Company</option>
                <option value="restoration">Restoration Company</option>
                <option value="multi-unit">Multi-Unit Residential</option>
                <option value="property-management">Property Management</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="mb-6">
              <label htmlFor="revenue" className="block text-sm font-semibold text-gray-700 mb-2">Annual Revenue Range</label>
              <select id="revenue" name="revenue" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm">
                <option value="">Select revenue range</option>
                <option value="under-1m">Under $1M</option>
                <option value="1m-3m">$1M - $3M</option>
                <option value="3m-5m">$3M - $5M</option>
                <option value="5m-10m">$5M - $10M</option>
                <option value="over-10m">Over $10M</option>
              </select>
            </div>

            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">Tell us about your challenges *</label>
              <textarea id="message" name="message" rows={5} required maxLength={500} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none" placeholder="What operational or financial challenges are you facing?"></textarea>
              <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
            </div>

            {formStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                Thank you! Your message has been sent successfully. We'll get back to you soon.
              </div>
            )}

            {formStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                Sorry, there was an error sending your message. Please try again.
              </div>
            )}

            <button type="submit" disabled={formStatus === 'submitting'} className="w-full px-8 py-4 bg-orange-600 text-white text-lg font-bold rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">
              {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Column 1 */}
            <div>
              <h4 className="text-lg font-bold mb-4">Services</h4>
              <div className="w-10 h-0.5 bg-orange-600 mb-6"></div>
              <ul className="space-y-3 text-sm text-gray-400">
                <li><button onClick={() => scrollToSection('services')} className="hover:text-white transition-colors">Operational Structure</button></li>
                <li><button onClick={() => scrollToSection('services')} className="hover:text-white transition-colors">Financial Control</button></li>
                <li><button onClick={() => scrollToSection('services')} className="hover:text-white transition-colors">Project Execution</button></li>
                <li><button onClick={() => scrollToSection('services')} className="hover:text-white transition-colors">Growth Strategy</button></li>
              </ul>
            </div>

            {/* Column 2 */}
            <div>
              <h4 className="text-lg font-bold mb-4">Method</h4>
              <div className="w-10 h-0.5 bg-orange-600 mb-6"></div>
              <ul className="space-y-3 text-sm text-gray-400">
                <li><button onClick={() => scrollToSection('methodology')} className="hover:text-white transition-colors">Diagnostic Phase</button></li>
                <li><button onClick={() => scrollToSection('methodology')} className="hover:text-white transition-colors">Framework Build</button></li>
                <li><button onClick={() => scrollToSection('methodology')} className="hover:text-white transition-colors">Implementation</button></li>
                <li><button onClick={() => scrollToSection('methodology')} className="hover:text-white transition-colors">Stability & Growth</button></li>
              </ul>
            </div>

            {/* Column 3 */}
            <div>
              <h4 className="text-lg font-bold mb-4">Company</h4>
              <div className="w-10 h-0.5 bg-orange-600 mb-6"></div>
              <ul className="space-y-3 text-sm text-gray-400">
                <li><button onClick={() => scrollToSection('why-us')} className="hover:text-white transition-colors">Why StructureFirst</button></li>
                <li><button onClick={() => scrollToSection('clients')} className="hover:text-white transition-colors">Who We Work With</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-white transition-colors">Contact</button></li>
              </ul>
            </div>

            {/* Column 4 */}
            <div>
              <h4 className="text-lg font-bold mb-4">Location</h4>
              <div className="w-10 h-0.5 bg-orange-600 mb-6"></div>
              <p className="text-sm text-gray-400 leading-relaxed">
                Serving construction companies in Ottawa–Gatineau and surrounding areas.
              </p>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center gap-6">
            <div>
              <div className="text-3xl font-bold mb-1">STRUCTURE</div>
              <div className="text-3xl font-bold mb-2">FIRST</div>
              <div className="w-16 h-0.5 bg-orange-600 mb-2"></div>
              <div className="text-xs text-gray-500">Ottawa–Gatineau</div>
            </div>

            <div className="text-center md:text-right">
              <p className="text-sm text-gray-400 mb-3">© 2025 StructureFirst Consulting. All rights reserved.</p>
              <div className="flex items-center justify-center md:justify-end gap-4">
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center bg-gray-700 rounded-full hover:bg-orange-600 transition-colors">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="mailto:info@structurefirst.com" className="w-8 h-8 flex items-center justify-center bg-gray-700 rounded-full hover:bg-orange-600 transition-colors">
                  <i className="ri-mail-fill text-lg"></i>
                </a>
              </div>
              <div className="mt-4">
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-orange-500 transition-colors">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
